/**
 * Клиентское сжатие изображения перед отправкой на бэкенд.
 *
 * Зачем это здесь. Бэкенд с картинкой не делает ничего: `ImageUploader::uploadFromPost`
 * кладёт присланные байты в объектное хранилище как есть — ни сжатия, ни ресайза, ни
 * конвертации. Единственный предел — `client_max_body_size 5M` на nginx, и превышение
 * он отдаёт HTML-страницей 413, до приложения запрос не доходит. Фото с телефона
 * (8–12 МБ) в этот предел не влезает.
 *
 * Два ограничения задают алгоритм:
 *
 * 1. **Разрешение важно.** Фото игрока показывается не только миниатюрой: карусель
 *    составов в `modules/overlay-v2` подставляет его в шаблон на канве 1920×1080, и
 *    размер плейсхолдера задаёт дизайнер шаблона. Поэтому разрешение уменьшается
 *    последним, только когда качества не хватило.
 * 2. **Прозрачность обязана сохраниться.** Вырезанные по контуру фото и логотипы
 *    кладутся поверх фона шаблона, и перевод в JPEG ломает их необратимо. При наличии
 *    альфа-канала выход — WebP (его принимает и бэкенд, и fabric).
 *
 * Файл, который и так проходит по размеру, не трогается вовсе: возвращается тот же
 * объект `File` — исходный формат, разрешение, EXIF и прозрачность на месте.
 */

/** Выше этого размера файл идёт в обработку. Запас к 5 МБ nginx — на поля формы. */
export const COMPRESS_THRESHOLD_BYTES = 4 * 1024 * 1024;

/** Бюджет результата. Совпадает с порогом: цель — уложиться, а не ужать любой ценой. */
export const TARGET_MAX_BYTES = 4 * 1024 * 1024;

/** Ниже этой стороны не опускаемся — граница приличного вида в карусели на 1080p. */
export const MIN_MAX_SIDE = 1600;

export interface CompressImageOptions {
  thresholdBytes?: number;
  targetBytes?: number;
}

export interface ImageSize {
  width: number;
  height: number;
}

export interface CompressImageResult {
  /** Готовый файл: либо новый, либо исходный без изменений. */
  file: File;
  wasCompressed: boolean;
  /** Разрешение осталось прежним. Осмысленно только при `wasCompressed`. */
  keptResolution: boolean;
  originalBytes: number;
  resultBytes: number;
  /** Размеры известны только когда файл декодировался, то есть при сжатии. */
  originalSize?: ImageSize;
  resultSize?: ImageSize;
  outputType: string;
}

export type CompressImageFailureReason =
  /** Браузер не смог декодировать файл — битый файл или формат вроде HEIC. */
  | 'decode-failed'
  /** Канва не отдала данные. */
  | 'encode-failed'
  /** Даже на последней ступени лестницы результат не уложился в бюджет. */
  | 'too-large';

export class CompressImageError extends Error {
  constructor(readonly reason: CompressImageFailureReason, message?: string) {
    super(message ?? reason);
    this.name = 'CompressImageError';
  }
}

/**
 * Лестница компромиссов для форматов с потерями.
 *
 * Порядок принципиален: сначала падает качество при исходном разрешении, и только
 * потом уменьшается сторона. Телефонное фото обычно укладывается на первых двух
 * ступенях — то есть в исходном разрешении.
 */
const LOSSY_STEPS: { maxSide?: number; quality: number }[] = [
  { quality: 0.92 },
  { quality: 0.85 },
  { maxSide: 2560, quality: 0.85 },
  { maxSide: 2048, quality: 0.82 },
  { maxSide: MIN_MAX_SIDE, quality: 0.8 },
];

/**
 * Лестница для PNG: качество на него не влияет, остаётся только разрешение.
 * Используется, когда есть прозрачность, а WebP браузер кодировать не умеет.
 */
const LOSSLESS_STEPS = [2560, 2048, MIN_MAX_SIDE, 1200];

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/png': 'png',
};

/** Форматы, у которых в принципе может быть альфа-канал. У JPEG её нет никогда. */
const TYPES_WITH_POSSIBLE_ALPHA = ['image/png', 'image/webp', 'image/gif'];

/** Сторона, на которой проверяется прозрачность: полноразмерный ImageData не нужен. */
const ALPHA_PROBE_MAX_SIDE = 512;

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

/**
 * Декодирование с учётом EXIF-поворота.
 *
 * Канва теряет EXIF, поэтому ориентацию нужно применить на входе — иначе снятые
 * вертикально фото с iPhone лягут набок. `imageOrientation` поддерживают Chrome 79+,
 * Firefox 77+ и Safari 15+; там, где опции или самого `createImageBitmap` нет,
 * остаётся `<img>` без коррекции поворота.
 */
async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
    } catch {
      try {
        const bitmap = await createImageBitmap(file);
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
      } catch {
        // Падаем в вариант с <img>: он умеет меньше, но зато есть везде.
      }
    }
  }

  return decodeWithImageElement(file);
}

function decodeWithImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        close: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new CompressImageError('decode-failed', `Не удалось декодировать ${file.type || 'файл'}`));
    };

    image.src = url;
  });
}

function scaledSize(width: number, height: number, maxSide?: number): ImageSize {
  if (!maxSide) return { width, height };

  const longest = Math.max(width, height);
  if (longest <= maxSide) return { width, height };

  const ratio = maxSide / longest;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

function drawToCanvas(image: DecodedImage, size: ImageSize): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext('2d');
  if (!context) throw new CompressImageError('encode-failed', 'Канва недоступна');

  context.drawImage(image.source, 0, 0, size.width, size.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new CompressImageError('encode-failed', 'Канва не отдала данные'))),
      type,
      quality,
    );
  });
}

/**
 * Есть ли в изображении хоть один непрозрачный пиксель.
 *
 * Проверка идёт по уменьшенной копии: полноразмерный `ImageData` восьмимегапиксельного
 * фото — это две сотни мегабайт. При уменьшении прозрачная область остаётся
 * прозрачной, поэтому вырезанный фон определяется надёжно.
 */
function hasAlphaChannel(image: DecodedImage, sourceType: string): boolean {
  if (!TYPES_WITH_POSSIBLE_ALPHA.includes(sourceType)) return false;

  const probeSize = scaledSize(image.width, image.height, ALPHA_PROBE_MAX_SIDE);
  const canvas = drawToCanvas(image, probeSize);
  const context = canvas.getContext('2d');
  if (!context) return false;

  const { data } = context.getImageData(0, 0, probeSize.width, probeSize.height);
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 255) return true;
  }

  return false;
}

function renameForType(fileName: string, type: string): string {
  const extension = EXTENSION_BY_TYPE[type] ?? 'jpg';
  const base = fileName.replace(/\.[^./\\]+$/, '') || 'photo';
  return `${base}.${extension}`;
}

/**
 * Кодирование одной ступени.
 *
 * `canvas.toBlob` по спецификации молча отдаёт PNG, если запрошенный тип не
 * поддерживается, — поэтому реальный тип берётся из самого блоба, а не из запроса.
 */
async function encodeStep(image: DecodedImage, maxSide: number | undefined, type: string, quality?: number) {
  const size = scaledSize(image.width, image.height, maxSide);
  const canvas = drawToCanvas(image, size);
  const blob = await toBlob(canvas, type, quality);

  return { blob, size, actualType: blob.type || type };
}

export async function compressImage(file: File, options: CompressImageOptions = {}): Promise<CompressImageResult> {
  const { thresholdBytes = COMPRESS_THRESHOLD_BYTES, targetBytes = TARGET_MAX_BYTES } = options;

  // Проходит по размеру — не трогаем вовсе. Перекодировать то, что и так влезает,
  // значит терять качество и прозрачность без всякой пользы.
  if (file.size <= thresholdBytes) {
    return {
      file,
      wasCompressed: false,
      keptResolution: true,
      originalBytes: file.size,
      resultBytes: file.size,
      outputType: file.type,
    };
  }

  const image = await decodeImage(file);

  try {
    const originalSize: ImageSize = { width: image.width, height: image.height };
    const needsAlpha = hasAlphaChannel(image, file.type);
    const preferredType = needsAlpha ? 'image/webp' : 'image/jpeg';

    // Первая ступень заодно показывает, умеет ли браузер кодировать WebP: если нет,
    // `toBlob` молча вернёт PNG, и дальше идти придётся лестницей разрешений.
    const [firstStep, ...restLossySteps] = LOSSY_STEPS;
    const probe = await encodeStep(image, firstStep.maxSide, preferredType, firstStep.quality);
    const outputType = probe.actualType;

    if (probe.blob.size <= targetBytes) {
      return buildResult(file, probe.blob, originalSize, probe.size, outputType);
    }

    const steps =
      outputType === preferredType
        ? restLossySteps
        : LOSSLESS_STEPS.map((maxSide) => ({ maxSide, quality: undefined }));

    for (const step of steps) {
      const { blob, size } = await encodeStep(image, step.maxSide, outputType, step.quality);
      if (blob.size <= targetBytes) {
        return buildResult(file, blob, originalSize, size, outputType);
      }
    }

    throw new CompressImageError('too-large', 'Не удалось уложиться в бюджет размера');
  } finally {
    image.close();
  }
}

function buildResult(
  original: File,
  blob: Blob,
  originalSize: ImageSize,
  resultSize: ImageSize,
  outputType: string,
): CompressImageResult {
  const file = new File([blob], renameForType(original.name, outputType), {
    type: outputType,
    lastModified: Date.now(),
  });

  return {
    file,
    wasCompressed: true,
    keptResolution: resultSize.width === originalSize.width && resultSize.height === originalSize.height,
    originalBytes: original.size,
    resultBytes: file.size,
    originalSize,
    resultSize,
    outputType,
  };
}
